import {
  type DamageEntry,
  DamageTypeEnum,
  InjuredPersonTypeEnum,
  type Injuries
} from '@lmc/validations'
import { trimmed, withoutBlanks } from '../../stepValues'
import type { DamageEntryValue, InjuryEntryValue } from './injuryEntryValues'

type AppSection = { details?: string; hasBeenApplied?: boolean } | null

type InjurySection =
  | 'carersInjuries'
  | 'otherPartiesInjuries'
  | 'residentInjuries'
type DamageSection = 'damages' | 'damagesIntroducingRisk'

/** What the mobile app records for step 5: one yes/no section per kind. */
export type AppInjuryAnswers = Partial<
  Record<InjurySection | DamageSection, AppSection>
> & {
  noDamagesDetails?: string | null
  noInjuriesDetails?: string | null
}

type AppAnswer<Entry> = {
  details?: string
  entries: Array<Entry>
  hasBeenApplied: boolean
}

/**
 * Yes with an entry per section applied. No only when the app gave details
 * for it, because the backend writes every section as not applied whether or
 * not the app asked; otherwise unanswered.
 */
const answerFrom = <Section extends InjurySection | DamageSection, Entry>(
  answers: AppInjuryAnswers | null | undefined,
  toEntry: Record<Section, (details: string | undefined) => Entry>,
  noDetails: string | null | undefined
): AppAnswer<Entry> | undefined => {
  const entries = (Object.keys(toEntry) as Array<Section>)
    .filter((section) => answers?.[section]?.hasBeenApplied)
    .map((section) => toEntry[section](trimmed(answers?.[section]?.details)))
  if (entries.length > 0) {
    return { hasBeenApplied: true, entries }
  }
  const details = trimmed(noDetails)
  return details ? { hasBeenApplied: false, entries: [], details } : undefined
}

const injury =
  (personType: InjuredPersonTypeEnum) =>
  (details: string | undefined): InjuryEntryValue => ({
    personType,
    ...withoutBlanks({ details })
  })

const damage =
  (type: DamageTypeEnum) =>
  (details: string | undefined): DamageEntryValue => ({
    type,
    ...withoutBlanks({ details })
  })

/**
 * Step 5's injuries from what the app recorded. The sections are the backend's v1 injury flags read
 * back; `otherPartiesInjuries` comes back as an other party. A team member or
 * other party has no person yet, so the user picks one before Next.
 */
const injuriesFromApp = (answers: AppInjuryAnswers | null | undefined) =>
  answerFrom(
    answers,
    {
      carersInjuries: injury(InjuredPersonTypeEnum.teamMember),
      otherPartiesInjuries: injury(InjuredPersonTypeEnum.otherParty),
      residentInjuries: injury(InjuredPersonTypeEnum.serviceUser)
    },
    answers?.noInjuriesDetails
  )

/** Step 5's damages from what the app recorded, as `injuriesFromApp`. */
const damagesFromApp = (answers: AppInjuryAnswers | null | undefined) =>
  answerFrom(
    answers,
    {
      damages: damage(DamageTypeEnum.damageOnly),
      damagesIntroducingRisk: damage(DamageTypeEnum.ongoingHazard)
    },
    answers?.noDamagesDetails
  )

/** The case fields step 5's answers come from. */
export type Step5Case = AppInjuryAnswers & {
  completedSteps?: ReadonlyArray<string>
  damageEntries?: Array<DamageEntry>
  injuries?: Injuries
}

/**
 * Step 5's answers. Once the user has saved the step, the case's own. Before
 * that, opening the case may already have pre-filled them from the app, but
 * with the service user's injury only: the team member and other party
 * injuries the app recorded are added from its sections, to be completed.
 * `damages` is also the v1 section, so it only answers once the step is
 * saved; until then the pre-filled entries, or the app's sections, decide.
 */
export const step5Answers = (savedCase: Step5Case) => {
  if (savedCase.completedSteps?.includes('injuriesAndDamages')) {
    return {
      damages: savedCase.damages && {
        ...savedCase.damages,
        entries: savedCase.damageEntries ?? []
      },
      injuries: savedCase.injuries
    }
  }
  const prefilled = savedCase.injuries
  const fromApp = injuriesFromApp(savedCase)
  const prefilledTypes = new Set(
    (prefilled?.entries ?? []).map((entry) => entry.personType)
  )
  const injuries =
    prefilled?.hasBeenApplied && fromApp?.hasBeenApplied
      ? {
          ...prefilled,
          entries: [
            ...(prefilled.entries ?? []),
            ...fromApp.entries.filter(
              (entry) => !prefilledTypes.has(entry.personType)
            )
          ]
        }
      : (prefilled ?? fromApp)
  const damages = savedCase.damageEntries?.length
    ? { hasBeenApplied: true, entries: savedCase.damageEntries }
    : damagesFromApp(savedCase)

  return { damages, injuries }
}
